import { booleanAttribute } from "#ce/utils/props";
import { isCssUnit } from "#ce/utils/css.js";
import { sizePropValues } from "#ce/composables/useSize.js";

export const props = Object.freeze({
  /**
   * Indicator type. "circular" renders a spinner; "linear" renders a horizontal bar; "ellipsis" renders animated dots.
   *
   * @type {String}
   * @default "circular"
   * @values circular, linear, ellipsis
   * @storybook-control select
   */
  type: {
    type: String,
    default: "circular",
    validator: (value) => ["circular", "linear", "ellipsis"].includes(value),
  },

  /**
   * Stretch the linear bar to fill the parent's width. When `false`, the bar takes a fixed width that can be overridden with `width`. Ignored for circular and ellipsis types.
   *
   * @type {Boolean}
   * @default true
   * @storybook-control boolean
   */
  expanded: booleanAttribute(true),

  /**
   * Width in pixels when `expanded` is `false` (linear type only). Ignored when `expanded` is `true` or `type` is "circular" / "ellipsis".
   *
   * @type {Number}
   * @default 200
   * @storybook-control number
   */
  width: {
    type: Number,
    default: 200,
    validator: (value) => typeof value === "number" && value > 0,
  },

  /**
   * Size of the indicator. Accepts a predefined size token or any CSS length (rem, px, em, etc.).
   *
   * @type {String}
   * @default "medium"
   * @values micro, xxx-small, xx-small, x-small, small, medium, large, x-large, xx-large, xxx-large, display, x-display
   * @storybook-control select
   */
  size: {
    type: String,
    default: "medium",
    validator: (value) => {
      // Allow predefined sizes or custom CSS units
      if (sizePropValues.includes(value)) {
        return true;
      }
      // Allow custom CSS units (rem, px, em, etc.)
      return isCssUnit(value);
    },
  },

  /**
   * Theme colour for the indicator. Accepts a system colour or a custom value (hex, CSS variable).
   *
   * @type {String}
   * @default "primary"
   * @values primary, secondary, tertiary, warning, success
   * @storybook-control select
   */
  color: {
    type: String,
    default: "primary",
  },

  /**
   * Progress value (0–100) for determinate mode. Leave undefined to switch to indeterminate (animated) mode.
   *
   * @type {Number}
   * @default undefined
   * @storybook-control number
   */
  value: {
    type: Number,
    default: undefined,
  },
});
